"use client";

import { useEffect, useState } from "react";
import { AlertTriangle, BookOpen, ChevronDown } from "lucide-react";
import { DetailDrawer } from "@/components/ui/detail-drawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { TruncatedText } from "@/components/ui/truncated-text";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { captureGlobal } from "@/lib/analytics";
import { getScoreTone } from "@/lib/score-tone";
import type {
  CallScoringDetail,
  ScoringDeliveryMetric,
  ScoringLineItem,
  ScoringPrepMetric,
  ScoringValueTone,
} from "@/lib/call-scoring-types";
import { useCallScoringCallDetail } from "@/hooks/use-call-scoring-call-detail";

function scoreToneClass(tone: ScoringValueTone): string {
  if (tone === "success") return "text-success";
  if (tone === "warning") return "text-warning";
  if (tone === "danger") return "text-destructive";
  return "text-muted-foreground";
}

function badgeClassFromScoreLabel(scoreLabel: string): string {
  const numeric = Number.parseFloat(scoreLabel.split("/")[0] ?? "");
  const tone = getScoreTone(numeric, { warningMin: 2, successMin: 4 });
  if (tone === "success") return "bg-success/10 text-success border-success/20";
  if (tone === "warning") return "bg-warning/10 text-warning border-warning/20";
  return "bg-danger/10 text-danger border-danger/20";
}

function formatContributionForTooltip(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

interface FinalScoreBreakdownRowProps {
  rowTitle: string;
  scoreText: string;
  metrics: readonly { title: string; contributionValue: number }[];
}

function FinalScoreBreakdownRow({ rowTitle, scoreText, metrics }: FinalScoreBreakdownRowProps) {
  const total = metrics.reduce((sum, m) => sum + m.contributionValue, 0);
  const joinedParts = metrics
    .map((m) => `${m.title} contribution (${formatContributionForTooltip(m.contributionValue)})`)
    .join(" + ");

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex cursor-help items-center justify-between rounded-xl border-2 border-border bg-muted/50 px-4 py-3 transition-colors hover:bg-muted/70">
            <Text as="span" size="sm" weight="semibold" color="default">
              {rowTitle}
            </Text>
            <Text as="span" size="xl" weight="bold" color="default" className="tabular-nums">
              {scoreText}
            </Text>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="end" className="max-w-sm space-y-2">
          {metrics.length > 0 ? (
            <>
              <Text as="p" size="xs" weight="normal" className="leading-relaxed">
                <Text as="span" size="xs" weight="semibold" color="default">
                Sum of contributions (score × weight)
                </Text>
                <Text as="span" size="xs" weight="normal" color="muted">
                  {" "}= {joinedParts}
                </Text>
              </Text>
              <Text as="p" size="xs" className="tabular-nums leading-snug">
                <Text as="span" size="xs" weight="semibold" color="default">
                  Sum of contributions (out of 5)
                </Text>
                <Text as="span" size="xs" weight="normal" color="muted">
                  {": "}
                </Text>
                <Text as="span" size="xs" weight="semibold" color="default">
                  {formatContributionForTooltip(total)}
                </Text>
                <Text as="span" size="xs" weight="semibold" color="default">
                  {" "}/ 5
                </Text>
              </Text>
            </>
          ) : (
            <Text as="p" size="xs" weight="normal" color="muted" className="leading-relaxed">
              No per-dimension contributions returned for this score.
            </Text>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

  function prepLineField(value: unknown): string {
    return typeof value === "string" ? value.trim() : "";
  }

/** Text lines for a prep entry object: Challenge / Response, or `--` when both empty. */
function prepObjectLabelLines(label: Exclude<ScoringLineItem["label"], string>): string[] {
  const c = prepLineField(label.challenge);
  const r = prepLineField(label.response);
  const lines: string[] = [];
  if (c) lines.push(`Challenge: ${c}`);
  if (r) lines.push(`Response: ${r}`);
  return lines.length > 0 ? lines : ["--"];
}

function PrepLineTruncated({ text , maxLines = 2}: { text: string, maxLines?: number }) {
  return (
    <TruncatedText
      text={text}
      as="span"
      size="sm"
      color="default"
      maxLines={maxLines}
      className="block w-full"
    />
  );
}

function LineItemRow({ item }: { item: ScoringLineItem }) {
  const labelBlock =
    typeof item.label === "string" ? (
      <PrepLineTruncated text={item.label} />
    ) : (
      <div className="min-w-0 space-y-1">
        {prepObjectLabelLines(item.label).map((line, i) => (
          <PrepLineTruncated key={i} text={line} maxLines={1} />
        ))}
      </div>
    );

  return (
    <div className="flex items-center  justify-between gap-3 py-2 text-sm">
      <div className="min-w-0 flex-1">{labelBlock}</div>
      <Text
        as="span"
        size="sm"
        color="default"
        className={cn("shrink-0 tabular-nums", scoreToneClass(item.tone))}
      >
        {item.value}
      </Text>
    </div>
  );
}

interface MetricScoreWithTooltipsProps {
  scoreLabel: string;
  scoreTone: ScoringValueTone;
  weightLabel: string;
  contributionLabel: string;
}

function MetricScoreWithTooltips({
  scoreLabel,
  scoreTone,
  weightLabel,
  contributionLabel,
}: MetricScoreWithTooltipsProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Text
            as="span"
            size="sm"
            weight="semibold"
            color="default"
            className={cn(
              "tabular-nums cursor-help underline decoration-dotted underline-offset-2",
              scoreToneClass(scoreTone),
            )}
          >
            {scoreLabel}
          </Text>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm space-y-1">
          <Text as="p" size="xs" weight="medium" color="default">
            Score: {scoreLabel}
          </Text>
          <Text as="p" size="xs" weight="normal" color="muted">
            Weight: {weightLabel}
          </Text>
          <Text as="p" size="xs" weight="normal" color="muted">
            Score * Weight: {contributionLabel}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PrepMetricCard({
  metric,
}: {
  metric: ScoringPrepMetric;
}) {
  const [open, setOpen] = useState(metric.defaultOpen ?? false);
  const isAccordion = Boolean(metric.collapsible && (metric.lineItems?.length ?? 0) > 0);
  const hasNarrative = Boolean(metric.narrative);
  const showDivider = hasNarrative && !isAccordion;

  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-none">
      {isAccordion ? (
        <>
          <button
            type="button"
            className="flex w-full items-start gap-2 text-left"
            onClick={() => setOpen((prev) => !prev)}
            aria-expanded={open}
          >
            <ChevronDown
              className={cn(
                "mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
              strokeWidth={2.5}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Text as="span" size="sm" weight="semibold" color="default">
                  {metric.title}
                </Text>
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-end">
              <MetricScoreWithTooltips
                scoreLabel={metric.scoreLabel}
                scoreTone={metric.scoreTone}
                weightLabel={metric.weightLabel}
                contributionLabel={metric.contributionLabel}
              />
            </div>
          </button>
          {open && metric.lineItems?.length ? (
            <div className="mt-3 border-t border-border pl-6 pt-1">
              {metric.lineItems.map((item) => (
                <LineItemRow key={JSON.stringify(item.label)} item={item} />
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <Text as="span" size="sm" weight="semibold" color="default">
                {metric.title}
              </Text>
            </div>
            <div className="flex shrink-0 items-center justify-end">
              <MetricScoreWithTooltips
                scoreLabel={metric.scoreLabel}
                scoreTone={metric.scoreTone}
                weightLabel={metric.weightLabel}
                contributionLabel={metric.contributionLabel}
              />
            </div>
          </div>
          {showDivider ? (
            <>
              <div className="my-3 border-t border-border" />
              <Text as="p" size="sm" weight="normal" color="muted" className="leading-relaxed">
                {metric.narrative}
              </Text>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function DeliveryMetricCard({ metric }: { metric: ScoringDeliveryMetric }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-none">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <Text as="p" size="sm" weight="semibold" color="default">
            {metric.title}
          </Text>
          <Text as="p" size="sm" weight="normal" color="muted" className="leading-relaxed">
            {metric.description}
          </Text>
        </div>
        <div className="flex shrink-0 items-center sm:justify-end">
          <MetricScoreWithTooltips
            scoreLabel={metric.scoreLabel}
            scoreTone={metric.scoreTone}
            weightLabel={metric.weightLabel}
            contributionLabel={metric.contributionLabel}
          />
        </div>
      </div>
    </div>
  );
}

function ImproveNextSection({ items }: { items: CallScoringDetail["improvements"] }) {
  return (
    <div className="space-y-2">
      <Text as="p" size="sm" weight="semibold" color="default">
        What to Improve Next
      </Text>
      <div className="rounded-xl border border-border bg-card">
        {items.map((item, i) => (
          <div key={`${item.lead}-${item.emphasis}`}>
            {i > 0 ? <div className="mx-4 border-t border-border" /> : null}
            <div className="flex gap-3 p-4">
              <BookOpen
                className="mt-0.5 size-4 shrink-0 text-amber-700 dark:text-amber-600"
                strokeWidth={2.5}
              />
              <div className="min-w-0 space-y-1">
                <Text as="p" size="sm" weight="semibold" color="default">
                  {item.lead}
                  <Text as="span" size="sm" weight="normal" color="muted">
                    {" "}→{" "}
                  </Text>
                  <Text as="span" size="sm" weight="semibold" color="default" className="text-amber-700 dark:text-amber-500">
                    {item.emphasis}
                  </Text>
                </Text>
                <Text as="p" size="sm" weight="normal" color="muted">
                  {item.description}
                </Text>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

type ScoringTab = "preparation" | "delivery";

interface ScoringDetailsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meetingId: number;
}

export function ScoringDetailsDrawer({
  open,
  onOpenChange,
  meetingId,
}: ScoringDetailsDrawerProps) {
  const [tab, setTab] = useState<ScoringTab>("preparation");
  const {
    drawerData: data,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
    isTranscriptInsufficient,
  } = useCallScoringCallDetail(meetingId);

  useEffect(() => {
    if (open) setTab("preparation");
  }, [meetingId, open]);

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Scoring Details"
      side="right"
    >
      {isLoading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 rounded-xl border border-border bg-card p-4">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-20" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-11/12" />
          </div>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={`scoring-loading-${idx}`} className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-10/12" />
              </div>
            ))}
          </div>
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
            <div className="min-w-0 flex-1">
              <Text as="p" size="sm" weight="semibold" color="default" className="text-destructive">
                Unable to load scoring details
              </Text>
              <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
                {(error as Error | null)?.message || "Please try again."}
              </Text>
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => refetch()}
                  loading={isRefetching}
                >
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : !data ? (
        isTranscriptInsufficient ? (
          <div className="rounded-xl border border-border bg-card p-4">
            <Text as="p" size="sm" weight="semibold" color="default">
              Transcript is insufficient for scoring
            </Text>
            <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
              We need more transcript content to calculate detailed preparation and delivery scores.
            </Text>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-4">
            <Text as="p" size="sm" weight="semibold" color="default">
              No scoring data available
            </Text>
            <Text as="p" size="sm" weight="normal" color="muted" className="mt-1">
              We couldn&apos;t find scoring details for this call.
            </Text>
          </div>
        )
      ) : (
      <>
      <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-xl">
        <div className="border-r border-border py-4">
          <Text as="p" size="sm" weight="medium" color="muted" className="mb-2">
            Preparation
          </Text>
          <div className="flex flex-wrap items-center gap-2">
            <Text as="p" size="2xl" weight="bold" color="default" className="tabular-nums">
              {data.preparationScore}
            </Text>
            <Badge
              variant="secondary"
              className={cn("text-xs font-semibold", badgeClassFromScoreLabel(data.preparationScore))}
            >
              {data.preparationBadge.label}
            </Badge>
          </div>
        </div>
        <div className="p-4 ml-4">
          <Text as="p" size="sm" weight="medium" color="muted" className="mb-2">
            Delivery
          </Text>
          <div className="flex flex-wrap items-center gap-2">
            <Text as="p" size="2xl" weight="bold" color="default" className="tabular-nums">
              {data.deliveryScore}
            </Text>
            <Badge
              variant="secondary"
              className={cn("text-xs font-semibold", badgeClassFromScoreLabel(data.deliveryScore))}
            >
              {data.deliveryBadge.label}
            </Badge>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <Text as="p" size="base" weight="semibold" color="default" className="mb-2">
          {data.executiveSummaryTitle}
        </Text>
        <Text as="p" size="sm" weight="normal" color="muted" className="leading-relaxed">
          {data.executiveSummaryBody}
        </Text>
      </div>
      <ImproveNextSection items={data.improvements} />


      <div className="rounded-xl bg-muted/70 p-1">
        <div className="grid grid-cols-2 gap-1">
          <button
            type="button"
            className={cn(
              "rounded-lg py-2.5 font-normal transition-all",
              tab === "preparation"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => {
              setTab((prev) => {
                if (prev !== "preparation") {
                  captureGlobal("scoring_tab_switched", {
                    tab: "preparation",
                    call_id: meetingId,
                  });
                }
                return "preparation";
              });
            }}
          >
            <Text as="span" size="sm" weight="medium" color="default">
              Preparation
            </Text>
          </button>
          <button
            type="button"
            className={cn(
              "rounded-lg py-2.5 font-normal transition-all",
              tab === "delivery"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            onClick={() => {
              setTab((prev) => {
                if (prev !== "delivery") {
                  captureGlobal("scoring_tab_switched", {
                    tab: "delivery",
                    call_id: meetingId,
                  });
                }
                return "delivery";
              });
            }}
          >
            <Text as="span" size="sm" weight="medium" color="default">
              Delivery
            </Text>
          </button>
        </div>
      </div>

      {tab === "preparation" ? (
        <div className="space-y-3">
          {data.preparationMetrics.map((m) => (
            <PrepMetricCard key={m.id} metric={m} />
          ))}
          <FinalScoreBreakdownRow
            rowTitle="Final Prep Score"
            scoreText={data.finalPrepScore}
            metrics={data.preparationMetrics}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {data.deliveryMetrics.map((m) => (
            <DeliveryMetricCard key={m.id} metric={m} />
          ))}
          <FinalScoreBreakdownRow
            rowTitle="Final Delivery Score"
            scoreText={data.finalDeliveryScore}
            metrics={data.deliveryMetrics}
          />
        </div>
      )}

      </>
      )}
    </DetailDrawer>
  );
}
